import { BASE_Y, R_BODY, R_RIM, bowlSurfaceY } from "./dimensions";

// WGSL for the two passes the wheel needs: everything solid, then the avatar
// chips on top of it.

/** A number as a WGSL float literal — 1 has to be written 1.0 to be one. */
const f = (n: number) => n.toFixed(4);
/** How tall the wheel stands on the cloth, which is how far it throws. */
const WHEEL_HEIGHT = bowlSurfaceY(R_RIM) - BASE_Y;

/**
 * Shared per-frame state.
 *
 *   params : (highlighted field, glow 0…1, seconds, chip height)
 *   chip   : (chip radius, chip size, cos tilt, sin tilt)
 */
const GLOBALS = /* wgsl */ `
struct Globals {
  viewProj : mat4x4<f32>,
  camPos   : vec4<f32>,
  lightDir : vec4<f32>,
  params   : vec4<f32>,
  chip     : vec4<f32>,
};
@group(0) @binding(0) var<uniform> G : Globals;
`;

/** Exposure and sRGB encode, applied once at the very end of both shaders. */
const TONEMAP = /* wgsl */ `
fn tonemap(colour : vec3<f32>) -> vec3<f32> {
  // An exposure curve rather than a clamp: highlights on the brass roll off
  // instead of flattening into white discs.
  let mapped = vec3<f32>(1.0) - exp(-colour * 1.02);
  return pow(clamp(mapped, vec3<f32>(0.0), vec3<f32>(1.0)), vec3<f32>(1.0 / 2.2));
}
`;

export const SOLID_SHADER = /* wgsl */ `
${GLOBALS}

struct Object {
  model     : mat4x4<f32>,
  normalMat : mat4x4<f32>,
  tint      : vec4<f32>,
};
@group(1) @binding(0) var<uniform> O : Object;

struct VSOut {
  @builtin(position) position : vec4<f32>,
  @location(0) world  : vec3<f32>,
  @location(1) normal : vec3<f32>,
  @location(2) colour : vec3<f32>,
  @location(3) @interpolate(flat) fieldInfo : vec2<f32>,
  /** The vertex in the mesh's own frame, which is what the wood grain is cut
   *  in: grain taken from world position would swim across the turning head
   *  instead of going round with it. */
  @location(4) local : vec3<f32>,
};

@vertex
fn vs(
  @location(0) position : vec3<f32>,
  @location(1) normal   : vec3<f32>,
  @location(2) colour   : vec3<f32>,
  @location(3) fieldInfo     : vec2<f32>,
) -> VSOut {
  let world = O.model * vec4<f32>(position, 1.0);
  var out : VSOut;
  out.position = G.viewProj * world;
  out.world = world.xyz;
  out.normal = (O.normalMat * vec4<f32>(normal, 0.0)).xyz;
  out.colour = colour;
  out.fieldInfo = fieldInfo;
  out.local = position;
  return out;
}

${TONEMAP}

fn hash1(n : f32) -> f32 {
  return fract(sin(n * 12.9898) * 43758.5453);
}

/**
 * Rosewood, worked out rather than sampled — a texture for the bowl would be
 * a download, and the wheel has to come up on a phone the moment the page
 * does.
 *
 * A wheel's bowl is turned from staves glued up round a circle, so the grain
 * does not run one way across the whole dish: it steps at every joint, and
 * each stave is a slightly different piece of wood. That is what the two
 * frequencies and the per-stave tone below are for — broad figure you read as
 * the shape of the timber, fine lines you read as grain, and a darker line
 * where two staves meet.
 */
fn woodGrain(p : vec3<f32>) -> f32 {
  let r = length(p.xz);
  let a = atan2(p.z, p.x);
  let staves = 18.0;
  let u = (a / 6.2831853 + 0.5) * staves;
  let tone = hash1(floor(u));
  let wander = sin(a * staves * 3.0 + tone * 9.0) * 0.05;
  let fine = sin((r + wander) * (150.0 + tone * 70.0) + tone * 30.0);
  let broad = sin((r + wander) * 21.0 + tone * 12.0);
  let edge = min(fract(u), 1.0 - fract(u));
  let seam = 0.74 + 0.26 * smoothstep(0.0, 0.05, edge);
  let figure = 0.5 + 0.15 * fine + 0.22 * broad + 0.26 * (tone - 0.5);
  return clamp(figure, 0.0, 1.0) * seam;
}

fn hash2(p : vec2<f32>) -> f32 {
  return fract(sin(dot(p, vec2<f32>(127.1, 311.7))) * 43758.5453);
}

/** Smooth value noise — the one building block the cloth is made of. */
fn valueNoise(p : vec2<f32>) -> f32 {
  let i = floor(p);
  let f = fract(p);
  let u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash2(i), hash2(i + vec2<f32>(1.0, 0.0)), u.x),
    mix(hash2(i + vec2<f32>(0.0, 1.0)), hash2(i + vec2<f32>(1.0, 1.0)), u.x),
    u.y,
  );
}

/**
 * Baize, as a brightness to multiply the cloth's colour by.
 *
 * Three things at three scales, because that is what stops cloth reading as a
 * sheet of coloured paper. The nap is the broadest: the fibres lie in
 * directions, so the cloth pools light in patches far larger than the weave
 * and never sits at one flat value. Under it is a finer fibre noise, the
 * speckle you see from a foot away. Finest of all is the weave itself, two
 * sets of threads crossing.
 *
 * The footprint is how much of the table one pixel covers, and the weave is
 * faded out as that grows: a grid drawn finer than the pixels sampling it
 * doesn't read as cloth, it crawls with moiré every time the wheel turns.
 */
fn baize(p : vec2<f32>, footprint : f32) -> f32 {
  // The nap, which is the broadest of the three: cloth is brushed, so the
  // fibres lie in a direction and the light pools in patches drawn out along
  // it — hence the stretched second term rather than a third round patch.
  let nap = valueNoise(p * 4.5) * 0.5
          + valueNoise(vec2<f32>(p.x * 24.0, p.y * 3.2)) * 0.3
          + valueNoise(p * 13.0) * 0.2;
  let fibre = valueNoise(p * 52.0);
  let fibreFade = 1.0 - smoothstep(0.01, 0.055, footprint);
  let weave = sin(p.x * 150.0) * sin(p.y * 150.0);
  let weaveFade = 1.0 - smoothstep(0.004, 0.02, footprint);
  // The amounts are large for what is meant to be a subtle material, and they
  // have to be: the cloth is dark, and a dark colour compresses hard on the
  // way through the tone curve into sRGB, so a modulation that looks sensible
  // as a number comes out as two or three levels of green on screen.
  return 0.74
       + 0.52 * nap
       + 0.3 * (fibre - 0.5) * fibreFade
       + 0.13 * weave * weaveFade;
}

/**
 * How much of the key light reaches a point on the cloth: 0 in the wheel's
 * shadow, 1 in the open.
 *
 * Worked out rather than rendered from a shadow map, which for one object of
 * a known shape is a lot of machinery for an answer that is two lines of
 * geometry. Push the wheel's silhouette along the light onto the table and
 * what you get is its widest circle swept from where the wheel stands to
 * where the top of the rim lands — a capsule — so the question is only how
 * far the point is from that segment.
 *
 * The penumbra widens along the throw, the way a real one does: the shadow is
 * crisp where the wheel meets the cloth and soft by the far end of itself.
 */
fn wheelShadow(p : vec2<f32>, L : vec3<f32>) -> f32 {
  let tip = -L.xz * (${f(WHEEL_HEIGHT)} / max(L.y, 0.1));
  let along = clamp(dot(p, tip) / max(dot(tip, tip), 0.0001), 0.0, 1.0);
  let d = length(p - tip * along);
  let soft = 0.05 + 0.3 * along;
  return smoothstep(${f(R_BODY)} - soft, ${f(R_BODY)} + soft, d);
}

@fragment
fn fs(in : VSOut) -> @location(0) vec4<f32> {
  // Culling is off — a lathe-built wheel has surfaces you can legitimately see
  // the inside of — so every normal is turned to face the viewer before it is
  // lit. Doing it from the view direction rather than from @builtin's winding
  // is what makes the bowl come out right: which side of a lathed surface the
  // triangles happen to wind towards is an accident of how the profile was
  // written, and getting it wrong doesn't darken the surface, it lights it
  // from an angle that has nothing to do with where you are looking from.
  let V = normalize(G.camPos.xyz - in.world);
  var N = normalize(in.normal);
  if (dot(N, V) < 0.0) { N = -N; }
  let L = normalize(G.lightDir.xyz);
  let material = in.fieldInfo.y;
  // How much of the world one pixel covers here. Taken now, before anything
  // branches on the material: a screen-space derivative may only be asked for
  // in uniform control flow, and which material a fragment is isn't uniform.
  let footprint = max(fwidth(in.world.x), fwidth(in.world.z));

  var base = in.colour;
  var ambient = 0.30;
  var specular = 0.0;
  var shininess = 32.0;
  // Strength of the glancing-angle sheen. It has to be per material rather
  // than one constant: the felt runs away from the camera to a very grazing
  // angle, where even a weak fresnel term turns the whole table into flat grey
  // haze — which is exactly what it did before this was split out.
  var sheen = 0.0;
  // …and what colour that sheen is. Everything hard on the wheel is polished,
  // so it throws back the light itself; the cloth doesn't, and takes its own
  // colour instead.
  var sheenColour = vec3<f32>(0.9, 0.85, 0.8);

  if (material < 0.5) {
    // Baize. No highlight at all — cloth has none — so everything that makes
    // it look like a surface rather than a background colour has to come from
    // the cloth itself and from how the light falls across the table.
    ambient = 0.34;
    let p = in.world.xz;
    let d = length(p);
    base = base * baize(p, footprint);
    // Cloth has no highlight, but it does have a nap, and a nap catches the
    // light where you see it edge-on. In the cloth's own colour, not in white:
    // a white sheen over green baize is the grey haze this used to have.
    sheenColour = base * 0.9;
    sheen = 0.22;
    // The shadow the wheel throws, and the darkness gathered in the angle
    // where it meets the cloth. Two different things: the second is close
    // contact, and it goes all the way round the foot whichever way the light
    // happens to be coming from.
    let contact = 0.5 + 0.5 * smoothstep(${f(R_BODY)} - 0.06, ${f(R_BODY)} + 0.3, d);
    // Cool, not merely dark. The key light is warm, so what is left in the
    // shadow is the blue fill — and a shadow painted in plain black is the
    // other half of why cloth reads as paper.
    let shadow = wheelShadow(p, L);
    base = base * contact * mix(vec3<f32>(0.34, 0.42, 0.44), vec3<f32>(1.0), shadow);
    // And the fall of the light across the table, which is what stops the far
    // side being the same green as the near side. Along the key light, not out
    // from the middle: a table lit from its own centre is a vignette, and a
    // vignette is the thing that made this read as a flat backdrop.
    base = base * (1.0 + 0.1 * clamp(dot(p, normalize(L.xz)) * 0.42, -1.0, 1.0));
  } else if (material < 1.5) {
    // Lacquer: the painted pockets and the number band. Hard, tight highlight
    // over a colour that stays deep — it is paint under varnish, not plastic.
    ambient = 0.19; specular = 0.62; shininess = 96.0;  sheen = 0.12;
  } else if (material < 2.5) {
    ambient = 0.22; specular = 1.35; shininess = 80.0;  sheen = 0.20;   // brass
  } else if (material < 3.5) {
    ambient = 0.40; specular = 2.10; shininess = 150.0; sheen = 0.30;   // the ball
  } else if (material < 4.5) {
    // Rosewood under polish: the grain darkens and lightens the timber, and
    // the varnish over it takes the same tight highlight the lacquer does.
    ambient = 0.21; specular = 0.22; shininess = 165.0; sheen = 0.12;
    base = base * (0.66 + 0.74 * woodGrain(in.local));
  } else {
    // Steel. Cooler than the brass, and deliberately not the brightest thing
    // on the wheel: a fret is a thin blade seen edge-on from here, and steel
    // lit as hard as the brass turns 22 of them into 22 white bars standing
    // in front of the pockets they are supposed to be dividing.
    ambient = 0.20; specular = 0.85; shininess = 150.0; sheen = 0.16;
  }

  let key = max(dot(N, L), 0.0);
  // A cool, dim fill from behind and to the left, so nothing on the far side
  // of the wheel falls into a black silhouette.
  let fillDir = normalize(vec3<f32>(-0.55, 0.40, -0.75));
  let fill = max(dot(N, fillDir), 0.0);
  let halfway = normalize(L + V);
  let spec = pow(max(dot(N, halfway), 0.0), shininess) * specular;
  let rim = pow(1.0 - max(dot(N, V), 0.0), 3.0) * sheen;

  let keyColour  = vec3<f32>(1.00, 0.96, 0.90);
  let fillColour = vec3<f32>(0.60, 0.70, 0.84);

  var lit = base * (ambient + key * 0.88) * keyColour
          + base * fill * 0.26 * fillColour
          + spec * keyColour
          + rim * sheenColour;

  // The winning pocket lifts out of the wheel rather than being pointed at.
  if (abs(in.fieldInfo.x - G.params.x) < 0.5) {
    lit = lit + base * G.params.y * 1.60 + vec3<f32>(0.34, 0.24, 0.07) * G.params.y;
  }

  lit = lit * O.tint.rgb + O.tint.a;
  return vec4<f32>(tonemap(lit), 1.0);
}
`;

export const CHIP_SHADER = /* wgsl */ `
${GLOBALS}

@group(1) @binding(0) var chipSampler : sampler;
@group(1) @binding(1) var chipTexture : texture_2d_array<f32>;

struct VSOut {
  @builtin(position) position : vec4<f32>,
  @location(0) uv : vec2<f32>,
  @location(1) @interpolate(flat) layer : i32,
  @location(2) @interpolate(flat) glow : f32,
  @location(3) normal : vec3<f32>,
};

@vertex
fn vs(
  @builtin(vertex_index) vertexIndex : u32,
  // (angle round the wheel, texture layer, scale, glow)
  @location(0) instance : vec4<f32>,
) -> VSOut {
  var corners = array<vec2<f32>, 6>(
    vec2<f32>(-0.5, -0.5), vec2<f32>(0.5, -0.5), vec2<f32>(0.5, 0.5),
    vec2<f32>(-0.5, -0.5), vec2<f32>(0.5, 0.5), vec2<f32>(-0.5, 0.5),
  );
  let corner = corners[vertexIndex];

  let angle = instance.x;
  let size = G.chip.y * instance.z;
  let c = cos(angle);
  let s = sin(angle);
  let centre = vec3<f32>(G.chip.x * c, G.params.w, G.chip.x * s);

  // The plate lies flat in the pocket and turns with the wheel, set radially
  // with its top towards the middle — the way the numbers are painted on a
  // real wheel. Its own "up" is therefore the inward radius, and its "right"
  // runs against the direction of travel: that is the pair whose cross product
  // points up out of the wheel, which is what leaves the picture the right way
  // round rather than mirrored when you look down on it.
  let inward = vec3<f32>(-c, 0.0, -s);
  let across = vec3<f32>(s, 0.0, -c);

  var out : VSOut;
  let world = centre + across * (corner.x * size) + inward * (corner.y * size);
  out.position = G.viewProj * vec4<f32>(world, 1.0);
  out.uv = vec2<f32>(corner.x + 0.5, 0.5 - corner.y);
  out.layer = i32(instance.y);
  out.glow = instance.w;
  out.normal = vec3<f32>(0.0, 1.0, 0.0);
  return out;
}

${TONEMAP}

@fragment
fn fs(in : VSOut) -> @location(0) vec4<f32> {
  let texel = textureSample(chipTexture, chipSampler, in.uv, in.layer);
  if (texel.a < 0.02) { discard; }

  let N = normalize(in.normal);
  let key = max(dot(N, normalize(G.lightDir.xyz)), 0.0);
  var lit = texel.rgb * (0.62 + 0.52 * key);
  lit = lit + texel.rgb * in.glow * 0.40 + vec3<f32>(0.16, 0.12, 0.03) * in.glow;
  return vec4<f32>(tonemap(lit), texel.a);
}
`;
